import * as yup from "yup";

function readEnglishLocalizedText(value) {
  if (typeof value === "string") return String(value || "").trim();
  if (Array.isArray(value)) {
    const englishEntry = value.find(
      (entry) => entry && typeof entry === "object" && "en" in entry,
    );
    return String(englishEntry?.en || "").trim();
  }
  if (value && typeof value === "object") {
    const englishEntry = value?.en;
    if (typeof englishEntry === "string") return englishEntry.trim();
    if (englishEntry && typeof englishEntry === "object") {
      return String(englishEntry?.value || "").trim();
    }
  }
  return "";
}

function readAnyLocalizedText(value) {
  if (typeof value === "string") return String(value || "").trim();
  if (Array.isArray(value)) {
    for (const entry of value) {
      if (!entry || typeof entry !== "object") continue;
      const [iso] = Object.keys(entry);
      if (!iso) continue;
      const localizedValue = String(entry?.[iso] || "").trim();
      if (localizedValue) return localizedValue;
    }
    return "";
  }
  if (value && typeof value === "object") {
    for (const entry of Object.values(value)) {
      if (typeof entry === "string" && entry.trim()) return entry.trim();
      if (entry && typeof entry === "object") {
        const localizedValue = String(entry?.value || "").trim();
        if (localizedValue) return localizedValue;
      }
    }
  }
  return "";
}

export function isNewRoutePoint(point = {}) {
  return !(
    point?.isPersisted ||
    point?.internalId ||
    point?.serverId ||
    point?.uuid ||
    !String(point?.id || "").startsWith("point-")
  );
}

export function hasRoutePointName(value, point = {}) {
  if (isNewRoutePoint(point)) {
    return Boolean(readEnglishLocalizedText(value));
  }
  return Boolean(readAnyLocalizedText(value));
}

export function hasRoutePointRadius(value) {
  const radius = Number(value ?? 0);
  return Number.isFinite(radius) && radius > 0;
}

export function hasRoutePointTurnCase(value) {
  return Number.isFinite(Number(value));
}

function hasRequiredMedia(value) {
  const media = Array.isArray(value?.multimedia) ? value.multimedia : [];
  const images = Array.isArray(value?.images) ? value.images : [];
  const audios = Array.isArray(value?.audios) ? value.audios : [];
  const mediaCountFromMultimedia = media.filter((item) => {
    const type = String(item?.mediaType || "").toLowerCase();
    const mime = String(item?.mimeType || "").toLowerCase();
    return (
      type === "image" ||
      type === "audio" ||
      mime.startsWith("image/") ||
      mime.startsWith("audio/")
    );
  }).length;
  return images.length + audios.length + mediaCountFromMultimedia >= 1;
}

export function hasRoutePointMedia(value) {
  return hasRequiredMedia(value);
}

export function hasRoutePointType(value, mode = "") {
  if (String(mode || "").toLowerCase() !== "random") return true;
  return Boolean(String(value || "").trim());
}

export const routePointRequiredSchema = yup.object({
  selectedMode: yup.string().default(""),
  name: yup
    .mixed()
    .test("required-name", "EN name is required.", (value, context) =>
      hasRoutePointName(value, context.parent),
    ),
  radiusMeters: yup
    .number()
    .typeError("Radius must be greater than 0.")
    .moreThan(0, "Radius must be greater than 0.")
    .required("Radius must be greater than 0."),
  turnCase: yup
    .number()
    .typeError("Turn Type is required.")
    .required("Turn Type is required."),
  multimedia: yup
    .mixed()
    .test(
      "required-media",
      "Add at least 1 image or audio.",
      (_, context) => hasRequiredMedia(context.parent),
    ),
  pointType: yup.mixed().test(
    "required-point-type",
    "Point Type is required.",
    (value, context) =>
      hasRoutePointType(value, context.parent?.selectedMode || ""),
  ),
});

export function hasInvalidRoutePoint(point = {}) {
  return !routePointRequiredSchema.isValidSync(
    {
      selectedMode: point?.selectedMode || point?.mode || "",
      id: point?.id || "",
      isPersisted: Boolean(point?.isPersisted),
      internalId: point?.internalId || "",
      serverId: point?.serverId || "",
      uuid: point?.uuid || "",
      name: point?.name,
      radiusMeters: point?.radiusMeters ?? point?.radius_meters ?? 0,
      turnCase: point?.turnCase ?? point?.turn_case,
      multimedia: point?.multimedia,
      images: point?.images,
      audios: point?.audios,
      pointType: point?.pointType ?? point?.point_type ?? "",
    },
    { abortEarly: false },
  );
}

export function hasInvalidRoutePoints(pointsList = []) {
  const list = Array.isArray(pointsList) ? pointsList : [];
  if (!list.length) return true;
  return list.some((point) => hasInvalidRoutePoint(point));
}
